'use client';

import { FC, useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { Content, asText, isFilled } from '@prismicio/client';
import { Job } from '@/interfaces/workable';
import { useWheelToHorizontalScroll, useWorkableJobs } from '@/hooks';
import { toJobCardModel } from '@/utils/workable';

type WithHeaderLightCardsSlice = Extract<
  Content.CardGridSlice,
  { variation: 'withHeaderLightCards' }
>;

export interface WithHeaderLightCardsProps {
  slice: WithHeaderLightCardsSlice;
  initialJobs: Job[];
}

const FallbackLocationIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 16 16"
    fill="none"
    width="16"
    height="16"
    aria-hidden="true"
  >
    <path
      d="M7.99967 7.66732C7.55765 7.66732 7.13372 7.49172 6.82116 7.17916C6.5086 6.8666 6.33301 6.44268 6.33301 6.00065C6.33301 5.55862 6.5086 5.1347 6.82116 4.82214C7.13372 4.50958 7.55765 4.33398 7.99967 4.33398C8.4417 4.33398 8.86562 4.50958 9.17819 4.82214C9.49075 5.1347 9.66634 5.55862 9.66634 6.00065C9.66634 6.21952 9.62323 6.43625 9.53947 6.63846C9.45572 6.84067 9.33295 7.0244 9.17819 7.17916C9.02342 7.33393 8.83969 7.45669 8.63748 7.54045C8.43527 7.62421 8.21854 7.66732 7.99967 7.66732ZM7.99967 1.33398C6.762 1.33398 5.57501 1.82565 4.69984 2.70082C3.82467 3.57599 3.33301 4.76297 3.33301 6.00065C3.33301 9.50065 7.99967 14.6673 7.99967 14.6673C7.99967 14.6673 12.6663 9.50065 12.6663 6.00065C12.6663 4.76297 12.1747 3.57599 11.2995 2.70082C10.4243 1.82565 9.23735 1.33398 7.99967 1.33398Z"
      fill="#041118"
    />
  </svg>
);

const FallbackClockIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 16 16"
    fill="none"
    width="16"
    height="16"
    aria-hidden="true"
  >
    <path
      d="M7.99967 13.9993C4.87013 13.9993 2.33301 11.4622 2.33301 8.33268C2.33301 5.20314 4.87013 2.66602 7.99967 2.66602C11.1292 2.66602 13.6663 5.20314 13.6663 8.33268C13.6663 11.4622 11.1292 13.9993 7.99967 13.9993ZM8.51483 4.9842H7.48452V8.54595L9.54513 10.6066L10.2736 9.87814L8.51483 8.11941V4.9842Z"
      fill="#041118"
    />
  </svg>
);

const JobCardSkeleton = () => (
  <div className="bg-[#F3F3F3] flex flex-col p-6 rounded-lg shrink-0 w-[303px] min-h-[277px] animate-pulse font-manrope">
    <div className="h-[63px] bg-black/10 rounded-[5px] w-full" />
    <div className="mt-4 h-5 bg-black/10 rounded w-3/4" />
    <div className="mt-2 mb-6 flex gap-6">
      <div className="h-4 bg-black/10 rounded w-1/3" />
      <div className="h-4 bg-black/10 rounded w-1/3" />
    </div>
    <div className="mt-auto self-start h-12 bg-black/10 rounded-[8px] w-24" />
  </div>
);

const WithHeaderLightCards: FC<WithHeaderLightCardsProps> = ({
  slice,
  initialJobs,
}) => {
  const { theme, title, subtitle, button_label, location_icon, time_icon } =
    slice.primary;
  const isDark = theme !== 'light';
  const buttonLabel =
    button_label && button_label.trim() ? button_label : 'Apply';
  const cardImages =
    slice.primary.items
      ?.map((item) => (isFilled.image(item.image) ? item.image.url : null))
      .filter((url): url is string => url !== null) ?? [];
  const { jobs, isLoading } = useWorkableJobs({ initialJobs });
  const cards = useMemo(() => jobs?.map(toJobCardModel) ?? [], [jobs]);
  const railRef = useRef<HTMLDivElement>(null);
  useWheelToHorizontalScroll(railRef);

  // Fade the right-edge gradient only: it hides when the rail is scrolled to
  // (or past) its end so it reads as "end of list". The left fade is always
  // on — the `pl-[30px]` on the rail keeps the first card clear of it.
  const [atEnd, setAtEnd] = useState(true);
  useEffect(() => {
    const el = railRef.current;
    if (!el) {
      return;
    }

    const update = () => {
      const remaining = el.scrollWidth - el.clientWidth - el.scrollLeft;
      setAtEnd(remaining <= 1);
    };

    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [isLoading, jobs]);

  const hasTitle = isFilled.richText(title);
  const hasSubtitle = isFilled.richText(subtitle);

  const sectionClass = isDark
    ? 'bg-[#262626] text-white'
    : 'bg-white text-[#041118]';

  // Left fade is always on; the right fade only appears when there is more
  // content to scroll to.
  const buildMaskGradient = (fadeEnd: boolean) =>
    `linear-gradient(to right, transparent 0%, black 30px, ${
      fadeEnd ? 'black calc(100% - 30px), transparent 100%' : 'black 100%'
    })`;
  const renderRow = (children: React.ReactNode) => {
    const mask = buildMaskGradient(!atEnd);
    return (
      <div
        className="relative overflow-hidden transition-[-webkit-mask-image,mask-image] duration-200"
        style={{ WebkitMaskImage: mask, maskImage: mask }}
      >
        <div
          ref={railRef}
          // `pl-[30px]` keeps the first card clear of the left fade zone (also
          // 30px) so it never renders behind the mask.
          className="flex gap-6 items-stretch overflow-x-auto no-scrollbar pl-[5px]"
        >
          {children}
        </div>
      </div>
    );
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`relative overflow-hidden px-5 py-16 md:px-[30px] md:py-20 lg:py-[70px] ${sectionClass} font-manrope`}
    >
      <div className="relative mx-auto w-full max-w-[1280px] flex flex-col gap-6">
        {(hasTitle || hasSubtitle) && (
          <h2 className="text-balance break-words text-[28px] font-medium leading-[1.2] md:text-[42px] max-w-[900px]">
            {hasTitle && (
              <span className={isDark ? 'text-[#05f273]' : 'text-[#aaa]'}>
                {asText(title)}
              </span>
            )}
            {hasTitle && hasSubtitle && <br />}
            {hasSubtitle && (
              <span className={isDark ? 'text-white' : 'text-[#041118]'}>
                {asText(subtitle)}
              </span>
            )}
          </h2>
        )}

        {isLoading
          ? renderRow(
              Array.from({ length: 4 }).map((_, i) => (
                <JobCardSkeleton key={i} />
              )),
            )
          : cards.length > 0 &&
            renderRow(
              cards.map((card, index) => {
                const cardBgUrl =
                  cardImages.length > 0
                    ? cardImages[index % cardImages.length]
                    : undefined;

                return (
                  <div
                    key={card.key}
                    className="bg-[#F3F3F3] flex flex-col p-6 rounded-lg shrink-0 w-[303px] min-h-[277px] text-[#041118]"
                  >
                    {cardBgUrl && (
                      <div className="h-[63px] relative overflow-hidden rounded-[5px] shrink-0 w-full">
                        <div
                          className="absolute left-0 w-full"
                          style={{ height: '238%', top: '-33.07%' }}
                        >
                          <Image
                            src={cardBgUrl}
                            alt=""
                            fill
                            sizes="303px"
                            className="object-cover"
                          />
                        </div>
                      </div>
                    )}
                    <p className="mt-6 font-bold text-[16px] leading-[1.5]">
                      {card.title}
                    </p>
                    <div className="mt-2 mb-6 flex gap-6 items-center">
                      {card.location && (
                        <div className="flex flex-1 gap-1 items-center min-w-0">
                          {isFilled.image(location_icon) ? (
                            <Image
                              src={location_icon.url}
                              alt=""
                              width={16}
                              height={16}
                              className="w-4 h-4"
                            />
                          ) : (
                            <FallbackLocationIcon />
                          )}
                          <span className="text-[14px] font-medium leading-[1.5] whitespace-nowrap">
                            {card.location}
                          </span>
                        </div>
                      )}
                      {card.type && (
                        <div className="flex flex-1 gap-1 items-center min-w-0">
                          {isFilled.image(time_icon) ? (
                            <Image
                              src={time_icon.url}
                              alt=""
                              width={16}
                              height={16}
                              className="w-4 h-4"
                            />
                          ) : (
                            <FallbackClockIcon />
                          )}
                          <span className="text-[14px] font-medium leading-[1.5] whitespace-nowrap">
                            {card.type}
                          </span>
                        </div>
                      )}
                    </div>
                    <a
                      href={card.applyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group mt-auto self-start inline-flex items-center justify-center gap-2 rounded-[8px] border border-[#041118] bg-transparent px-6 py-3 font-inter text-[16px] font-medium leading-[1.5] text-[#041118] transition-colors"
                    >
                      <span className="group-hover:underline">
                        {buttonLabel}
                      </span>
                    </a>
                  </div>
                );
              }),
            )}
      </div>
    </section>
  );
};

export default WithHeaderLightCards;
